import type { AuthContextState } from '../AuthContext';
import type { AuthResult } from '../sessionClient';

export const DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE = 'Sorry, something went wrong.';

const defaultWritePathResult: AuthResult<never> = {
  success: false,
  error: { code: 'unknown', message: '' },
};

/**
 * The value `AuthContext` is created with, and therefore the value `useContext`
 * returns when no provider is mounted above. Its object identity doubles as the
 * "no provider" sentinel for `useProvidedContext` / `useOptionalAuth`: detecting
 * a missing provider by identity works even though the default is a fully
 * populated (logged-out, inert-write-path) object rather than `undefined`.
 *
 * Do not barrel-export this, and never pass it to a `<AuthContext.Provider>` —
 * doing either would defeat the sentinel check.
 */
export const initAuthContext: AuthContextState = {
  authentication: {
    state: 'not_authenticated',
    user: null,
  },
  logout: () => {},
  refresh: () => {},
  verifyLogin: async () => ({
    success: false,
    displayErrorMessage: DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE,
  }),
  validateResetPasswordToken: async () => ({ success: true }),
  resetPassword: async () => ({ success: true }),
  logIn: async () => defaultWritePathResult,
  register: async () => defaultWritePathResult,
  loginWithPasswordlessToken: async () => defaultWritePathResult,
  requestHumanStatus: async () => defaultWritePathResult,
  requestPasswordReset: async () => defaultWritePathResult,
  upsertMarketingSmsConsent: async () => ({ success: false, reason: 'error' }),
};
